import App from 'app/app.js';
import 'app/action/LocaleChangeAction.js';
import 'app/action/SessionTimeoutAction.js';
import 'app/action/ContentChangeAction.js';
import 'app/service/BlockService.js';
import 'app/store/MessageStore.js';
import 'app/store/UserProfileStore.js';
import 'app/service/ContentJobTypeMappingService.js';
import 'app/store/ProductCatalogStore.js';

const alphanumericComparator = (a, b) => a.localeCompare(b, 'en', { numeric: true });

// eslint-disable-next-line max-lines-per-function
function ContentStore(
  $http,
  $rootScope,
  $window,
  translateProviderService,
  JobStore,
  LocaleChangeAction,
  SessionTimeoutAction,
  ContentChangeAction,
  MessageStore,
  BlockService,
  UserProfileStore,
  ContentJobTypeMappingService,
  ProductCatalogStore,
  LookAllowlistService,
  $filter
) {
  this.cdnUrl = $window.cdnUrl;

  var contentMap = {},
    productImages = {},
    grailsConfig = {},
    enums = {},
    contentCache = {},
    contentFailureKey = 'CONTENT_ERROR_FAILURE';

  let lookDefinitions = [];
  let backgroundImages = [];
  let seasons = [];
  let skuSetsList = [];
  let skuSetsMap = {};
  const BUNDLE_IMAGE = 'BUNDLE_IMAGE';

  this.getBackgroundImages = function () {
    return backgroundImages;
  };
  this.getLookDefinitions = function () {
    return lookDefinitions;
  };
  this.getSeasons = function () {
    return seasons;
  };
  this.getSkuSet = function (name) {
    return name ? skuSetsMap[name.toUpperCase()] : null;
  };

  function clearAll() {
    contentMap = {};
    productImages = {};
    grailsConfig = {};
    enums = {};
    contentCache = {};
    lookDefinitions = [];
    backgroundImages = [];
    seasons = [];
    skuSetsList = [];
    skuSetsMap = {};
  }

  function handleErrors(data) {
    clearAll();
    if (data && data.errors) {
      data.errors.forEach(function (error) {
        MessageStore.createSystem(error.key || contentFailureKey, error.params);
      });
    } else {
      MessageStore.createSystem(contentFailureKey);
    }
  }

  function getEnums(enums) {
    var retVal = {};

    function convertToMap(itemList) {
      var map = {};

      itemList.forEach(function (item) {
        map[item.name] = item;
      });

      return map;
    }

    R.forEach(function (key) {
      retVal[key] = {
        code: convertToMap(enums[key]),
        values: enums[key]
      };
    })(R.keys(enums));

    return retVal;
  }

  this.zones = {
    FIND_PICTURE_DAY: 'FIND_PICTURE_DAY',
    MOBILE_IMAGE_STORE: 'MOBILE_IMAGE_STORE',
    MOBILE_STORE_PROMO_ONE: 'MOBILE_STORE_PROMO_ONE',
    MOBILE_STORE_PROMO_TWO: 'MOBILE_STORE_PROMO_TWO',
    FEATURED_PACKAGE_TEXT: 'FEATURED_PACKAGE_TEXT',
    INFO_BAR_SUBJECT_TEXT: 'INFO_BAR_SUBJECT_TEXT',
    MOBILE_ADDON_PROMO: 'MOBILE_ADDON_PROMO',
    WHATS_THIS_TEXT: 'WHATS_THIS_TEXT',
    FAQ_CATEGORIES: 'FAQ_CATEGORIES',
    FAQ_ENTRIES: 'FAQ_ENTRIES',
    MOBILE_PAY_ONLY_IMAGE: 'MOBILE_PAY_ONLY_IMAGE',
    MOBILE_ENHANCEMENT_IMAGES: 'MOBILE_ENHANCEMENT_IMAGES',
    HOME_IMAGE_AUTO_SLIDER: 'HOME_IMAGE_AUTO_SLIDER',
    SUGGESTIVE_SELLING: 'SUGGESTIVE_SELLING'
  };

  this.getSeason = (jobContext) => {
    const schoolYear = new Date(jobContext.eventDate).getFullYear();

    return seasons.find((season) => season.subprogramName === jobContext.jobName && season.year === schoolYear);
  };

  this.preloadContent = function () {
    BlockService.blockUI();
    return window.contentPromise
      .then(function (response) {
        return response.json();
      })
      .then((response) => this.parseContentResponse(response))
      .finally(function () {
        BlockService.unblockUI();
      });
  };

  this.parseContentResponse = (data) => {
    if (data.success) {
      contentMap = data.data.contentMap;
      productImages = data.data.productImages;
      grailsConfig = data.data.grailsConfig;
      enums = getEnums(data.data.enums);
      backgroundImages = data.data.backgroundImages;
      skuSetsList = data.data.skuSets;
      skuSetsMap = data.data.skuSets.reduce(
        (acc, skuSet) => ({
          ...acc,
          [skuSet.name.toUpperCase()]: skuSet
        }),
        {}
      );
      seasons = this._initSeasons(data.data.seasons || []);
      // sort to avoid backend order
      lookDefinitions = data.data.lookDefinitions?.sort(
        (a, b) => alphanumericComparator(a.crop, b.crop) || a.preferredClick - b.preferredClick
      );
      ContentChangeAction.dispatch(contentMap);
      contentCache = {};
      $rootScope.$broadcast('contentChanged');
    } else {
      handleErrors(data);
    }
  };

  this._initSeasons = (seasons) =>
    seasons.map((season) => ({
      ...season,
      freeShippingMeterUpsells: (season.freeShippingMeterUpsells || []).sort(
        (a, b) => b.minCostThreshold - a.minCostThreshold
      ),
      addOnCategories: (season.addOnCategories || []).map((category) => ({
        ...category,
        image: this.resolveContentImagePath(`/addOnCategory/${category.image}`)
      })),
      ...LookAllowlistService.getAllowlists(season, skuSetsMap)
    }));

  this.getFreeShippingMeterUpsellsSkus = (subtotal) => {
    const currentSeason = JobStore.getCurrentSeason();
    if (!currentSeason) {
      return [];
    }

    const selectedUpsell =
      currentSeason?.freeShippingMeterUpsells?.find((upsell) => upsell.minCostThreshold <= subtotal) || {};
    return selectedUpsell?.skuSets?.map((name) => this.getSkuSet(name)) || [];
  };

  this.getAllSkuSetsAsList = () => {
    return skuSetsList;
  };

  this.getProductSkuSetsAsList = () => {
    return this.getAllSkuSetsAsList().filter((set) => set.category === 'PRODUCT');
  };

  this.loadContent = function () {
    BlockService.blockUI();
    var localeParam = translateProviderService.getLocale();

    return $http({ method: 'GET', url: 'content/' + localeParam + '?lang=' + localeParam.replace('-', '_') })
      .then(
        function (response) {
          this.parseContentResponse(response.data);
        }.bind(this)
      )
      .catch(
        function (response) {
          handleErrors(response.data);
        }.bind(this)
      )
      ['finally'](
        function () {
          BlockService.unblockUI();
        }.bind(this)
      );
  };

  this.onLocaleChange = function (type, args) {
    this.loadContent();
  };

  this.onSessionTimeout = function (type, args) {
    clearAll();
  };

  /**
   * Method for returning the mapping from the content tool to the correct resource. It takes a Zone in which the
   * content resources should reside, and the code for the respective resource. If there is a job loaded it adds
   * the subprogram and then iterates through the content map to see if the resource exists. If not it uses the
   * non subprogram version of the code and returns the resource if it matches.
   * BANNER_MOBILE_SPORTS.
   * @param zone, required, Resource zone
   * @param code, required, Resource code
   * @returns contentResource
   */
  this.getContent = function (zone, code) {
    if (UserProfileStore.getEmail() === this.getConfig().mltContentTranslationAdmin) {
      return code;
    }

    var resultArray = [],
      key;
    var subprogramCode = JobStore.isJobLoaded() ? code + '_' + ContentJobTypeMappingService.mapSubprogram() : undefined;
    var subprogramKeyFound = false;

    var cacheKey = zone + '_' + code;
    if (subprogramCode) {
      cacheKey = cacheKey + '_' + subprogramCode;
    }
    var cachedContent = contentCache[cacheKey];
    if (cachedContent !== undefined) {
      return cachedContent;
    }

    /**
     * For each key in the contentTool search if the subprogram code matches a resource in them, if it does, set
     * subprogram key to true and return the subprogram resource. if not return the default resource that the code represents.
     */
    for (key in contentMap) {
      if (contentMap.hasOwnProperty(key) && (!zone || key === zone)) {
        R.forEach(function (resource) {
          if (resource.code === subprogramCode) {
            subprogramKeyFound = true;
            resultArray.push(resource);
          } else {
            if (resource.code === code) {
              resultArray.push(resource);
            }
          }
        })(contentMap[key].zoneDetailDTOList);
      }
    }
    // If we found a subprogram resource for our code
    if (subprogramKeyFound) {
      var subprogramContentItem = R.find(R.propEq('code', subprogramCode))(resultArray);
      if (subprogramContentItem) {
        contentCache[cacheKey] = subprogramContentItem.contentItem;
        return subprogramContentItem.contentItem;
      }
    }
    // If the resource exists but there is no subprogram for it
    if (resultArray.length > 0) {
      var defaultContentItem = R.find(R.propEq('code', code))(resultArray);
      if (defaultContentItem) {
        contentCache[cacheKey] = defaultContentItem.contentItem;
        return defaultContentItem.contentItem;
      }
    }
    // If zone + code resource doesnt exist in MLT content return nothing.
    return undefined;
  };

  /**
   * Translate a content key with optional parameter interpolation.
   * Supports zone.key syntax and _$COUNTRY suffix for country-specific content.
   * @param {string} key - The content key to translate (can be "zone.key" format)
   * @param {Object} [params] - Optional parameters for interpolation (e.g., { name: 'John' })
   * @returns {string} The translated content, or the key if not found
   */
  this.translate = (key, params) => {
    let zone = null;
    let translationKey = key;

    if (key.includes('.')) {
      const [zonepart, keyPart] = key.split('.');
      zone = zonepart;
      translationKey = keyPart;
    }

    if (translationKey.endsWith('_$COUNTRY')) {
      translationKey = this.appendCountryCode(translationKey.replace('_$COUNTRY', ''));
    }

    let content = this.getContent(zone, translationKey);
    if (!content) {
      return translationKey;
    }

    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        const regex = new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g');
        content = content.replace(regex, paramValue);
      });
    }

    return content;
  };

  this.getContentAsNumber = function (zone, code) {
    const content = this.getContent(zone, code);
    return parseFloat(content) || undefined;
  };

  /**
   * Method to append logged in or logged out to a resource code.
   * @param code
   * @returns {string}
   */
  this.appendLogin = function (code) {
    if (UserProfileStore.isLoggedIn()) {
      code = code + '_LOGGED_IN';
    } else {
      code = code + '_LOGGED_OUT';
    }
    return code;
  };

  /**
   * Method to append CYOP yes or no to a resource code.
   * @param code
   * @returns {string} or nothing if there is no job loaded
   */
  this.appendCyop = function (code) {
    //Checks the JobStore to make sure the job is loaded so we do not break the website
    if (JobStore.isJobLoaded()) {
      if (ProductCatalogStore.containsCyop()) {
        code = code + '_CYOP';
      } else {
        code = code + '_NO_CYOP';
      }
      return code;
    }
  };

  /**
   * Method to append the country code to a resource code.
   * Checks if it is a canadian domain, if it is it adds _CA. Otherwise it add's _US.
   * @param code
   * @returns {string}
   */
  this.appendCountryCode = function (code) {
    if (translateProviderService.isCanadianDomain()) {
      code = code + '_CA';
    } else {
      code = code + '_US';
    }
    return code;
  };

  this.getProductImages = function () {
    return productImages;
  };

  this.getMobileImageStore = function () {
    var i,
      mobileImageStore = [];
    // populate mobileImageStore

    R.forEach(function (image) {
      mobileImageStore[image.code] = image.contentItem;
    }, this.getZoneDetails(this.zones.MOBILE_IMAGE_STORE));

    return mobileImageStore;
  };

  this.getProductCategories = function () {
    return this.getZoneDetails('PRODUCT_CATEGORY') || [];
  };

  this.getZoneDetails = function (zone, code) {
    if (zone === this.zones.SUGGESTIVE_SELLING && !this.getConfig().enableSuggestiveSelling) {
      return undefined;
    }
    if (
      contentMap &&
      contentMap[zone] &&
      contentMap[zone].zoneDetailDTOList &&
      contentMap[zone].zoneDetailDTOList.length > 0
    ) {
      return contentMap[zone].zoneDetailDTOList;
    }
    return undefined;
  };

  this.getSelectionTemplateField = function (templateName, fieldName) {
    return this.getContent(templateName, fieldName);
  };

  this.getConfig = function () {
    return grailsConfig;
  };

  this.getEnum = function (key) {
    return enums[key] || {};
  };

  this.getProductImage = function (skuCode, defaultLayoutThemeId) {
    var imgNA = 'assets/images/productImageNA_' + translateProviderService.getLocale() + '.png';
    this.productImages = this.getProductImages() || [];
    this.productImg = this.productImages[skuCode + '_' + (defaultLayoutThemeId || '')];
    this.imgSrc =
      !this.productImg || !this.productImg.image
        ? imgNA
        : this.resolveContentImagePath(`/productImages/${this.productImg.image}`);
    return this.imgSrc;
  };

  // will fallback on small image if large is not available
  this.getLargeProductImage = function (skuCode, defaultLayoutThemeId) {
    var imgNA = 'assets/images/productImageNA_' + translateProviderService.getLocale() + '.png';
    this.productImages = this.getProductImages() || [];
    var productImg = this.productImages[skuCode + '_' + (defaultLayoutThemeId || '')];
    return !productImg || !(productImg.largeImage || productImg.image)
      ? imgNA
      : this.resolveContentImagePath(`/productImages/${productImg.largeImage || productImg.image}`);
  };

  this.getProductDescription = function (skuCode, defaultLayoutThemeId) {
    var productImages = this.getProductImages() || [],
      productImg = productImages[skuCode + '_' + (defaultLayoutThemeId || '')];

    return !!productImg ? this.getContent(null, productImg.whatsThisCode) : null;
  };

  this.getProductImageData = function (orderEnhancement) {
    if (!orderEnhancement?.orderable) {
      return {
        image: null,
        largeImage: null,
        whatsThisCode: null,
        whatsThisContent: null
      };
    }

    const { skuCode, defaultLayoutThemeId } = orderEnhancement.orderable;
    const themeId = defaultLayoutThemeId || '';

    const productImg = productImages[`${skuCode}_${themeId}`];

    const image = this.getProductImage(skuCode, defaultLayoutThemeId);
    const largeImage = this.getLargeProductImage(skuCode, defaultLayoutThemeId);

    const whatsThisCode = productImg?.whatsThisCode || null;
    const whatsThisContent = whatsThisCode ? this.getContent(null, whatsThisCode) : null;

    return {
      image,
      largeImage,
      whatsThisCode,
      whatsThisContent
    };
  };

  this.getProductImageDataForBundle = function (skuCode) {
    if (!skuCode) {
      return undefined;
    }

    const bundleImage = this.getBundleImage(skuCode);

    return {
      image: bundleImage,
      largeImage: bundleImage,
      whatsThisContent: $filter('ltTranslateFilter')(`BUNDLE_ROMANCE_${skuCode}`)
    };
  };

  this.getBundleImage = function (skuCode, zone = BUNDLE_IMAGE) {
    if (!skuCode) {
      return;
    }

    let bundleImage = this.getContent(zone, `${BUNDLE_IMAGE}_${skuCode}`);

    if (!bundleImage && zone !== BUNDLE_IMAGE) {
      bundleImage = this.getContent(BUNDLE_IMAGE, `${BUNDLE_IMAGE}_${skuCode}`);
    }

    return bundleImage && this.resolveContentImagePath(bundleImage);
  };

  this.getSurveyURL = function (jobCountry) {
    if (!jobCountry) {
      return;
    }

    var surveyUrl = this.getContent('CUSTOMER_SURVEY', jobCountry);
    if (!UserProfileStore.getLifetouchUserProfileId()) {
      return surveyUrl;
    }
    if (surveyUrl.indexOf('userProfileID=not_provided') > 0) {
      return surveyUrl
        .split('userProfileID=not_provided')
        .join('userProfileID=' + UserProfileStore.getLifetouchUserProfileId());
    }
    var suffix = surveyUrl.indexOf('?') > 0 ? '&' : '?';
    return surveyUrl + suffix + 'userProfileID=' + UserProfileStore.getLifetouchUserProfileId();
  };

  this.getContentImageUrl = function (zone, code) {
    return this.resolveContentImagePath(this.getContent(zone, code));
  };

  this.resolveContentImagePath = function (contentPath) {
    return `${this.cdnUrl}mltdata${contentPath}`;
  };

  this.isPcbCompatible = function (jobData) {
    if (jobData.jobContext.jobName === 'Sports' || !jobData.availableLooks?.looks) {
      return false;
    }

    const uniqueVMLooks = this.getUniqueLayoutThemeIDs(jobData.availableLooks.looks);
    const uniqueContentLooks = new Set(this.getLookDefinitions().map((lookDefinition) => lookDefinition.layoutThemeId));

    const missingContentLooks = new Set([...uniqueVMLooks].filter((elem) => !uniqueContentLooks.has(elem)));

    if (missingContentLooks.size > 0) {
      console.warn(
        'PCB incompatible job: the following look definitions are missing in the content store:',
        Array.from(missingContentLooks)
      );
    }

    return missingContentLooks.size === 0;
  };

  this.getUniqueLayoutThemeIDs = function (looks) {
    return new Set(
      Object.values(looks)
        .flat()
        .map((look) => look.layoutThemeID)
    );
  };

  this.getImageForZoneKeyThemeId = function (zone, key, defaultLayoutThemeId) {
    if (!defaultLayoutThemeId) {
      return this.getImageForZoneKey(zone, key);
    }
    const themeKey = `${key}_${defaultLayoutThemeId}`;
    return this.getImageForZoneKey(zone, themeKey);
  };

  this.getImageForZoneKey = function (zone, key) {
    const content = this.getContent(zone, key);
    if (!content) {
      return undefined;
    }

    const altText = this.getContent(zone, `${key}_ALT`);

    return {
      src: this.resolveContentImagePath(content),
      altText: altText
    };
  };

  this.resolveProductImage = (skuCode, themeId) => {
    const map = this.getProductImages() ?? {};
    const entry = map[`${skuCode}_${themeId ?? ''}`];
    if (!entry?.image) return undefined;
    return {
      src: this.resolveContentImagePath(`/productImages/${entry.image}`),
      largeSrc: entry.largeImage ? this.resolveContentImagePath(`/productImages/${entry.largeImage}`) : undefined,
      whatsThisContent: entry.whatsThisCode ? this.getContent(null, entry.whatsThisCode) : undefined,
      source: 'product'
    };
  };

  this.resolveContentImage = (zone, code) => {
    const path = this.getContent(zone, code);
    if (!path) return undefined;
    const altText = this.getContent(zone, `${code}_ALT`);
    return {
      src: this.resolveContentImagePath(path),
      altText: altText || undefined,
      source: 'content-zone'
    };
  };

  this.resolveTranslationImage = (translationKey) => {
    const path = this.translate(translationKey);
    if (!path || path === translationKey) return undefined;
    return {
      src: this.resolveContentImagePath(path),
      source: 'content-zone'
    };
  };

  LocaleChangeAction.register(this.onLocaleChange.bind(this));
}

App.service('ContentStore', [
  '$http',
  '$rootScope',
  '$window',
  'TranslateProviderService',
  'JobStore',
  'LocaleChangeAction',
  'SessionTimeoutAction',
  'ContentChangeAction',
  'MessageStore',
  'BlockService',
  'UserProfileStore',
  'ContentJobTypeMappingService',
  'ProductCatalogStore',
  'LookAllowlistService',
  '$filter',
  ContentStore
]);
